import { useEffect, useRef, useState } from 'react';

export function useDebounce<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Update debounced value after delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cancel the timeout if value changes (also on delay change or unmount)
    // This is how we prevent debounced value from updating if value is changed
    // within the delay period. Timeout gets cleared and restarted.
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useDebounceWithCallback<T>(
  value: T,
  {
    delay = 500,
    callback,
  }: {
    delay?: number;
    callback?: (debouncedValue: T) => unknown;
  } = {},
) {
  const callbackRef = useRef(callback);

  useEffect(() => {
    const handler = setTimeout(() => {
      callbackRef.current?.(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
}
